import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';

import RefreshIcon from '@material-ui/icons/Refresh';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import FormComponent from '../../components/FormComponent';
import FMPHeadline from '../../components/ui/FMPHeadline';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import FMPStatusIcon from '../../components/ui/FMPStatusIcon';
import FMPSelect from '../../components/pureForm/FMPSelect';
import EnhancedTable from '../../components/table/EnhancedTable';
import SystemContainer from '../../containers/system/System';
import { getStatus, searchDaily } from '../../modules/system/tankDailySalesStatus';
import { getSharedStyles } from '../../themes';

const styles = (theme) => ({
	...getSharedStyles(theme),
	//- Same summary bar treatment as InventoryBalanceSync, so the two status screens read as
	//- the same kind of page.
	summaryBar: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		padding: '10px 16px',
		marginBottom: 12,
		background: '#fafafa',
		border: '1px solid #eee',
		borderRadius: 4,
	},
	summaryBarStat: {
		marginRight: 28,
	},
	summaryBarLabel: {
		fontSize: 11,
		textTransform: 'uppercase',
		letterSpacing: 1,
		color: '#888',
	},
	summaryBarValue: {
		fontSize: 18,
		fontWeight: 500,
	},
	summaryBarDivider: {
		width: 1,
		alignSelf: 'stretch',
		background: '#ddd',
		marginRight: 28,
	},
	healthReasons: {
		background: '#FFF8E1',
		border: '1px solid #FFE082',
		borderRadius: 4,
		padding: '8px 10px',
		marginBottom: 12,
		fontSize: 13,
	},
	label: {
		paddingRight: 5,
		textTransform: 'uppercase',
		fontSize: '85%',
		color: '#555',
	},
	errorValue: {
		color: '#f44336',
	},
	note: {
		fontSize: 13,
		color: '#666',
		marginTop: 6,
	},
	resultsHint: {
		fontSize: 12,
		color: '#888',
		marginBottom: 6,
	},
});

const dailyColumns = [
	{ sortId: 'saleDate', displayId: 'saleDateDisplay', label: 'Sale Date' },
	{ sortId: 'rowCount', displayId: 'rowCountDisplay', label: 'Records', numeric: true },
	{ sortId: 'stationCount', displayId: 'stationCount', label: 'Stations', numeric: true },
	{
		sortId: 'totalSalesVolume',
		displayId: 'totalSalesVolumeDisplay',
		label: 'Gallons',
		numeric: true,
	},
	{
		sortId: 'missingSalesVolumeCount',
		displayId: 'missingSalesVolumeDisplay',
		label: 'No Volume',
		numeric: true,
	},
	{
		sortId: 'missingInventoryCount',
		displayId: 'missingInventoryCount',
		label: 'Missing Inv.',
		numeric: true,
	},
	{ sortId: 'overrideCount', displayId: 'overrideCount', label: 'Overrides', numeric: true },
	{ sortId: 'anomalyCount', displayId: 'anomalyDisplay', label: 'Anomalies', numeric: true },
	{
		sortId: 'newestComputedAgeMinutes',
		displayId: 'newestDateComputedDisplay',
		label: 'Last Computed',
	},
];

const lookbackOptions = [
	{ value: 14, display: 'Last 14 days' },
	{ value: 30, display: 'Last 30 days' },
	{ value: 60, display: 'Last 60 days' },
	{ value: 90, display: 'Last 90 days' },
	{ value: 180, display: 'Last 180 days' },
];

//- Minutes -> something a person reads without counting zeros.
function formatAgeMinutes(minutes) {
	if (minutes == null) {
		return 'never';
	}
	if (minutes < 60) {
		return `${minutes}m ago`;
	}
	const hours = Math.floor(minutes / 60);
	if (hours < 24) {
		return `${hours}h ${minutes % 60}m ago`;
	}
	return `${Math.floor(hours / 24)}d ${hours % 24}h ago`;
}

function formatCount(value) {
	if (value == null) {
		return '-';
	}
	return Number(value).toLocaleString();
}

function formatGallons(value) {
	if (value == null) {
		return '-';
	}
	return Math.round(value).toLocaleString();
}

class TankDailySalesStatus extends FormComponent {
	constructor(props) {
		super(props);
		this.state = {
			status: undefined,
			isLoading: true,

			dailyRows: undefined,
			isLoadingDaily: true,
			lookbackDays: 30,
		};
	}

	componentDidMount() {
		this.loadStatus();
		this.loadDaily();
	}

	showError = (err) => {
		this.props.showErrors(_.isArray(err) ? err : [err]);
	};

	loadStatus = () => {
		this.setState({ isLoading: true });
		getStatus({
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoading: false });
					return this.showError(err);
				}
				this.setState({ status: r, isLoading: false });
			},
		});
	};

	loadDaily = () => {
		this.setState({ isLoadingDaily: true });
		searchDaily({
			lookbackDays: this.state.lookbackDays,
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoadingDaily: false });
					return this.showError(err);
				}
				this.setState({ isLoadingDaily: false, dailyRows: this.buildDailyRows(r) });
			},
		});
	};

	handleRefresh = () => {
		this.loadStatus();
		this.loadDaily();
	};

	buildDailyRows = (result) => {
		const collections = result.collections || {};

		return (collections.tankDailySalesDayCollection || []).map((row) => ({
			key: row.saleDate,

			//- Sort keys stay raw; only the *Display fields are formatted.
			saleDate: row.saleDate,
			saleDateDisplay: moment(row.saleDate, 'YYYY-MM-DD').format('ddd, M/D/YYYY'),

			rowCount: row.rowCount,
			rowCountDisplay: formatCount(row.rowCount),

			stationCount: row.stationCount,

			totalSalesVolume: row.totalSalesVolume,
			totalSalesVolumeDisplay: formatGallons(row.totalSalesVolume),

			missingSalesVolumeCount: row.missingSalesVolumeCount,
			missingSalesVolumeDisplay: (
				<span style={{ color: row.missingSalesVolumeCount > 0 ? '#FF9800' : undefined }}>
					{formatCount(row.missingSalesVolumeCount)}
				</span>
			),

			missingInventoryCount: row.missingInventoryCount,
			overrideCount: row.overrideCount,

			anomalyCount: row.anomalyCount,
			anomalyDisplay:
				row.anomalyCount > 0 ? (
					<span style={{ color: '#FF9800', whiteSpace: 'nowrap' }}>
						<WarningRoundedIcon style={{ fontSize: 16, verticalAlign: 'middle' }} />{' '}
						{formatCount(row.anomalyCount)}
					</span>
				) : (
					'-'
				),

			newestComputedAgeMinutes: row.newestComputedAgeMinutes,
			newestDateComputedDisplay: (
				<Tooltip title={row.newestDateComputed || ''}>
					<span>{moment(row.newestDateComputed).format('M/D h:mm a')}</span>
				</Tooltip>
			),
		}));
	};

	renderRow = (label, value, isError) => {
		const { classes } = this.props;
		return (
			<div style={{ marginTop: 4 }} key={label}>
				<span className={classes.label}>{label}: </span>
				<span className={isError ? classes.errorValue : undefined}>{value}</span>
			</div>
		);
	};

	renderSummaryBar = () => {
		const { classes } = this.props;
		const { status } = this.state;

		const stat = (label, value) => (
			<div className={classes.summaryBarStat} key={label}>
				<div className={classes.summaryBarLabel}>{label}</div>
				<div className={classes.summaryBarValue}>{value}</div>
			</div>
		);

		return (
			<div className={classes.summaryBar}>
				<div className={classes.summaryBarStat}>
					<div className={classes.summaryBarLabel}>Health</div>
					<div className={classes.summaryBarValue}>
						<FMPStatusIcon status={status.healthSeverity} title={status.health} /> {status.health}
					</div>
				</div>
				<div className={classes.summaryBarDivider} />
				{stat(
					'Last Run',
					status.lastRun.lastRunAt
						? moment(status.lastRun.lastRunAt).format('M/D h:mm a')
						: 'never'
				)}
				{stat('Records Written', formatCount(status.lastRun.rowsUpserted))}
				{stat(
					'Took',
					status.lastRun.tookSeconds == null ? '-' : `${status.lastRun.tookSeconds}s`
				)}
				<div className={classes.summaryBarDivider} />
				{stat('Total Records', formatCount(status.coverage.rowCount))}
				{stat('Newest Sales Day', status.coverage.lastSaleDateWithVolume || '-')}
			</div>
		);
	};

	renderDetails = () => {
		const { classes } = this.props;
		const { status } = this.state;
		const { lastRun, coverage, schedule } = status;

		return (
			<Grid container spacing={3}>
				<Grid item xs={12} md={6}>
					<FMPSectionHeader label={'Latest Generation'} isTop={true} />
					{this.renderRow(
						'Last Run At',
						lastRun.lastRunAt
							? `${moment(lastRun.lastRunAt).format('ddd, M/D/YYYY h:mm a')} (${formatAgeMinutes(
								lastRun.lastRunAgeMinutes
							)})`
							: 'Never'
					)}
					{this.renderRow('Records Written', formatCount(lastRun.rowsUpserted))}
					{this.renderRow(
						'Took',
						lastRun.tookSeconds == null ? '-' : `${lastRun.tookSeconds}s`
					)}
					{this.renderRow(
						'Range Covered',
						lastRun.windowStart ? `${lastRun.windowStart} to ${lastRun.windowEnd}` : '-'
					)}
					{lastRun.lastError && this.renderRow('Last Error', lastRun.lastError, true)}

					<FMPSectionHeader label={'Refresh Window'} />
					{this.renderRow('Next Run Covers', `${schedule.windowStart} to ${schedule.windowEnd}`)}
					{this.renderRow('Records In Window', formatCount(status.window.rowCount))}
					{this.renderRow('Days In Window', formatCount(status.window.dayCount))}
					{this.renderRow(
						'Rewritten This Interval',
						formatCount(status.window.rowsWrittenThisInterval)
					)}
				</Grid>

				<Grid item xs={12} md={6}>
					<FMPSectionHeader label={'Table Coverage'} isTop={true} />
					{this.renderRow('Total Records', formatCount(coverage.rowCount))}
					{this.renderRow('Stations', formatCount(coverage.stationCount))}
					{this.renderRow('Tanks', formatCount(coverage.tankCount))}
					{this.renderRow(
						'Sale Date Range',
						coverage.firstSaleDate ? `${coverage.firstSaleDate} to ${coverage.lastSaleDate}` : '-'
					)}
					{/*- Rows run to the end of the window whether or not a volume could be computed,
					    so this is the date that actually says how current the sales data is. */}
					{this.renderRow(
						'Newest Day With Sales',
						coverage.lastSaleDateWithVolume || 'none'
					)}
					{this.renderRow(
						'Newest Record Computed',
						coverage.newestDateComputed
							? `${moment(coverage.newestDateComputed).format('ddd, M/D h:mm a')} (${formatAgeMinutes(
								coverage.newestComputedAgeMinutes
							)})`
							: '-'
					)}
					{this.renderRow('Records Written (24h)', formatCount(coverage.rowsWrittenLast24Hours))}
					{this.renderRow('Total Gallons', formatGallons(coverage.totalSalesVolume))}

					<FMPSectionHeader label={'Data Quality'} />
					{this.renderRow(
						'Records With No Volume',
						formatCount(coverage.missingSalesVolumeCount)
					)}
					{this.renderRow('Missing Inventory', formatCount(coverage.missingInventoryCount))}
					{this.renderRow('Manual Overrides', formatCount(coverage.overrideCount))}
					{this.renderRow('Flagged Anomalies', formatCount(coverage.anomalyCount))}
					{this.renderRow('With A Data Note', formatCount(coverage.dataQualityNoteCount))}
				</Grid>

				<Grid item xs={12}>
					<div className={classes.note}>
						Runs every {schedule.intervalMinutes} minutes on the data importer, refreshing the last
						two full months plus the current one so late Veeder readings, delivery corrections and
						manual overrides are picked up. Sales for a day need the following day&apos;s reading,
						so the newest day this window can compute a volume for is{' '}
						{schedule.expectedLatestSaleDate} &mdash; records after that exist but stay empty until
						the reading arrives. Anything older than the window is settled and is only rewritten by
						the backfill script. The table is {String(status.productScope).toUpperCase()} only.
					</div>
				</Grid>
			</Grid>
		);
	};

	renderDailyTable = () => {
		const { classes } = this.props;
		const { dailyRows, isLoadingDaily } = this.state;

		return (
			<>
				<FMPSectionHeader label={'Records Per Day'} isTop={true} />
				<Grid container spacing={3}>
					<FMPSelect {...this.setForm('lookbackDays', 'Show')} md={3} options={lookbackOptions} />
					<Grid item xs={12} md={3} style={{ marginTop: 18 }}>
						<Button variant="contained" color="primary" onClick={this.loadDaily}>
							Apply
						</Button>
					</Grid>
				</Grid>

				{isLoadingDaily && <div className={classes.fetching}>Loading daily counts...</div>}
				{!isLoadingDaily && _.isEmpty(dailyRows) && (
					<div className={classes.resultsHint}>
						No records have been generated for this date range.
					</div>
				)}
				{!isLoadingDaily && !_.isEmpty(dailyRows) && (
					<EnhancedTable
						isDense={true}
						columns={dailyColumns}
						rows={dailyRows}
						defaultOrder={'desc'}
						defaultOrderBy={'saleDate'}
					/>
				)}
			</>
		);
	};

	render() {
		const { classes } = this.props;
		const { status, isLoading } = this.state;

		return (
			<div>
				<FMPHeadline label="Tableau Sales Data Generation" />

				{isLoading && <div className={classes.fetching}>Loading status...</div>}
				{!isLoading && !status && (
					<div className={classes.fetching}>Could not load the generation status.</div>
				)}

				{status && (
					<>
						{this.renderSummaryBar()}
						{!_.isEmpty(status.healthReasons) && (
							<div className={classes.healthReasons}>
								{status.healthReasons.map((reason) => (
									<div key={reason}>{reason}</div>
								))}
							</div>
						)}
						<div style={{ marginBottom: 12 }}>
							<Button size="small" startIcon={<RefreshIcon />} onClick={this.handleRefresh}>
								Refresh
							</Button>
						</div>

						<Paper className={classes.paper}>{this.renderDetails()}</Paper>
					</>
				)}

				<Paper className={classes.paper} style={{ marginTop: 20 }}>
					{this.renderDailyTable()}
				</Paper>
			</div>
		);
	}
}

TankDailySalesStatus.propTypes = {
	classes: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), SystemContainer])(TankDailySalesStatus);
